import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardHeader,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Badge,
} from "reactstrap";

import apiService from '../../../../service/apiService';
import apiConfig, { buildEndpointUrl } from '../../../../config/apiConfig';
import { AddressFormModal } from '../shared';
import NotificationBanner from '../../../../components/NotificationBanner';
import merchantService from '../../../../service/merchantService.ts';
// COMENTADO - Solo usar token de Keycloak, no useToken de TokenContext
// import { useToken } from '../../../../contexts/TokenContext';

const MerchantAddressesTab = ({ merchant }) => {
  // COMENTADO - Solo usar token de Keycloak, no useToken
  // const { token, hasToken } = useToken();
  const hasToken = () => apiService.hasToken();
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    addressType: 'PHYSICAL',
    country: ''
  });
  const [addingAddress, setAddingAddress] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  
  // Estados para editar dirección
  const [showEditAddressModal, setShowEditAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editAddressData, setEditAddressData] = useState({
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    addressType: 'PHYSICAL',
    country: '',
    phoneNumber: ''
  });
  const [updatingAddress, setUpdatingAddress] = useState(false);
  
  // Estados para eliminar dirección
  const [showDeleteAddressModal, setShowDeleteAddressModal] = useState(false);
  const [deletingAddress, setDeletingAddress] = useState(null);
  const [deletingAddressLoading, setDeletingAddressLoading] = useState(false);

  // Function to show notifications
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  // Function to close notification
  const closeNotification = () => {
    setNotification({ show: false, type: 'success', message: '' });
  };

  // Function to load addresses for the merchant
  const loadAddresses = async () => {
    if (!merchant || !merchant.id) return;
    
    setLoadingAddresses(true);
    try {
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.addresses(merchant.id));
      const data = await apiService.get(endpoint);
      
      let addressesData = data;
      if (data && data.content && Array.isArray(data.content)) {
        addressesData = data.content;
      } else if (data && Array.isArray(data)) {
        addressesData = data;
      } else if (data && data.addresses && Array.isArray(data.addresses)) {
        addressesData = data.addresses;
      } else {
        addressesData = [];
      }
      
      setAddresses(addressesData);
    } catch (error) {
      console.error('Error loading addresses:', error);
      setAddresses([]);
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Load addresses when component mounts or merchant changes
  useEffect(() => {
    loadAddresses();
  }, [merchant]);

  // Function to handle add address button click
  const handleAddAddress = () => {
    setShowAddAddressModal(true);
  };

  // Function to check if address type already exists
  // Now allows multiple addresses of the same type
  const getAvailableAddressTypes = () => {
    const allTypes = ['PHYSICAL', 'MAILING', 'BILLING'];
    return allTypes;
  };

  // Function to handle add address modal toggle
  const toggleAddAddressModal = () => {
    setShowAddAddressModal(!showAddAddressModal);
    if (!showAddAddressModal) {
      setNewAddress({
        street1: '',
        street2: '',
        city: '',
        state: '',
        zipCode: '',
        addressType: 'PHYSICAL',
        country: ''
      });
    }
  };

  // Function to handle new address input changes
  const handleNewAddressChange = (field, value) => {
    setNewAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to save new address
  const handleSaveNewAddress = async () => {
    if (!merchant || !merchant.id) return;

    if (!newAddress.street1) {
      showNotification('error', 'Please enter a street address');
      return;
    }

    setAddingAddress(true);
    try {
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.addresses(merchant.id));
      const createdAddress = await apiService.post(endpoint, newAddress);
      
      setAddresses(prev => [...prev, createdAddress]);
      
      setShowAddAddressModal(false);
      setNewAddress({
        street1: '',
        street2: '',
        city: '',
        state: '',
        zipCode: '',
        addressType: 'PHYSICAL',
        country: ''
      });
      
      showNotification('success', 'Address added successfully!');
    } catch (error) {
      console.error('Error creating address:', error);
      showNotification('error', 'Error creating address. Please try again.');
    } finally {
      setAddingAddress(false);
    }
  };

  // Function to get address display string
  const getAddressDisplay = (address) => {
    const parts = [address.street1, address.street2, address.city, address.state, address.zipCode, address.country].filter(Boolean);
    return parts.join(', ');
  };

  // Function to handle edit address button click
  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setEditAddressData({
      street1: address.street1 || '',
      street2: address.street2 || '',
      city: address.city || '',
      state: address.state || '',
      zipCode: address.zipCode || '',
      addressType: address.addressType || 'PHYSICAL',
      country: address.country || '',
      phoneNumber: address.phoneNumber || ''
    });
    setShowEditAddressModal(true);
  };

  // Function to handle edit address modal toggle
  const toggleEditAddressModal = () => {
    setShowEditAddressModal(!showEditAddressModal);
    if (!showEditAddressModal) {
      setEditingAddress(null);
      setEditAddressData({
        street1: '',
        street2: '',
        city: '',
        state: '',
        zipCode: '',
        addressType: 'PHYSICAL',
        country: '',
        phoneNumber: ''
      });
    }
  };

  // Function to handle edit address input changes
  const handleEditAddressChange = (field, value) => {
    setEditAddressData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to save edited address
  const handleSaveEditedAddress = async () => {
    if (!merchant || !merchant.id || !editingAddress) return;

    if (!editAddressData.street1) {
      showNotification('error', 'Please enter a street address');
      return;
    }

    setUpdatingAddress(true);
    try {
      if (!hasToken()) {
        showNotification('error', 'Authentication token required');
        return;
      }

      const token = await apiService.getToken();
      await merchantService.updateMerchantAddress(
        token,
        merchant.id,
        editingAddress.id,
        {
          street1: editAddressData.street1 || '',
          street2: editAddressData.street2 || '',
          city: editAddressData.city || '',
          state: editAddressData.state || '',
          country: editAddressData.country || '',
          zipCode: editAddressData.zipCode || '',
          addressType: editAddressData.addressType || 'PHYSICAL',
          phoneNumber: editAddressData.phoneNumber || ''
        }
      );
      
      // Update the address in the local state
      setAddresses(prev => prev.map(addr => 
        addr.id === editingAddress.id 
          ? { ...addr, ...editAddressData }
          : addr
      ));
      
      setShowEditAddressModal(false);
      setEditingAddress(null);
      
      showNotification('success', 'Address updated successfully!');
    } catch (error) {
      console.error('Error updating address:', error);
      showNotification('error', 'Error updating address. Please try again.');
    } finally {
      setUpdatingAddress(false);
    }
  };

  // Function to handle delete address button click
  const handleDeleteAddress = (address) => {
    setDeletingAddress(address);
    setShowDeleteAddressModal(true);
  };

  // Function to handle delete address modal toggle
  const toggleDeleteAddressModal = () => {
    setShowDeleteAddressModal(!showDeleteAddressModal);
    if (!showDeleteAddressModal) {
      setDeletingAddress(null);
    }
  };

  // Function to confirm delete address
  const handleConfirmDeleteAddress = async () => {
    if (!merchant || !merchant.id || !deletingAddress) return;

    setDeletingAddressLoading(true);
    try {
      if (!hasToken()) {
        showNotification('error', 'Authentication token required');
        return;
      }

      const token = await apiService.getToken();
      await merchantService.deleteMerchantAddress(
        token,
        merchant.id,
        deletingAddress.id
      );
      
      // Remove the address from the local state
      setAddresses(prev => prev.filter(addr => addr.id !== deletingAddress.id));
      
      setShowDeleteAddressModal(false);
      setDeletingAddress(null);
      
      showNotification('success', 'Address deleted successfully!');
    } catch (error) {
      console.error('Error deleting address:', error);
      showNotification('error', 'Error deleting address. Please try again.');
    } finally {
      setDeletingAddressLoading(false);
    }
  };

  return (
    <Container fluid>
      {/* Notification Banner */}
      <NotificationBanner
        type={notification.type}
        message={notification.message}
        show={notification.show}
        onClose={closeNotification}
      />
      
      <Card className="shadow-sm">
        <CardHeader style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb' }}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fas fa-map-marker-alt text-info" />
              <h5 className="mb-0 text-dark" style={{ fontSize: '1rem' }}>
                Business Addresses
              </h5>
            </div>
            <Button 
              color="primary" 
              size="sm" 
              onClick={handleAddAddress}
            >
              <i className="ni ni-plus mr-2" />
              Add Address
            </Button>
          </div>
        </CardHeader>

        <CardBody>


          {loadingAddresses ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="text-muted mt-2 mb-0">Loading addresses...</p>
            </div>
               ) : addresses.length === 0 ? (
                 <div className="text-center py-4">
                   <i className="fas fa-map-marker-alt text-muted" style={{ fontSize: '2rem' }}></i>
                   <p className="mt-2 text-muted">No addresses available</p>
                 </div>
               ) : (
                 <Row>
                   {addresses.map((address, index) => (
                   <Col md="6" key={address.id || index}>
                     <Card>
                       <CardHeader style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb'}}> 
                         <div className="d-flex align-items-center">
                           <i className="fas fa-home text-secondary" />
                           <Badge color="secondary">
                             {address.addressType}
                           </Badge>
                         </div>
                       </CardHeader>
                       <CardBody className="pb-3">
                         <div className="address-info">
                           <p className="mb-0 font-weight-bold">{address.country}, {address.state}, {address.city}, {address.zipCode}</p>
                           <p>
                             <i className="fas fa-map-marker-alt text-info mr-1"></i>
                             Phone Number: {address.phoneNumber}
                           </p>
                         </div>
                         <div className="address-actions mt-0">
                           <div className="d-flex gap-2">
                             <Button 
                               color="primary" 
                               size="sm" 
                               title="Edit address"
                               onClick={() => handleEditAddress(address)}
                             >
                               <i className="ni ni-settings-gear-65" />
                             </Button>
                             {/* <Button 
                               color="danger" 
                               size="sm"
                               title="Delete address"
                               onClick={() => handleDeleteAddress(address.id)}
                               disabled={deletingAddressLoading}
                             >
                               <i className="ni ni-fat-remove" />
                             </Button> */}
                           </div>
                         </div>
                       </CardBody>
                     </Card>
                   </Col>
                 ))}
                 </Row>
               )}
             </CardBody>
      </Card>



      {/* Add Address Modal */}
      <AddressFormModal
        isOpen={showAddAddressModal}
        toggle={toggleAddAddressModal}
        onSave={(addressData) => {
          // Add to local state and close modal
          setAddresses(prev => [...prev, addressData]);
          setShowAddAddressModal(false);
          showNotification('success', 'Address added successfully!');
        }}
        availableAddressTypes={getAvailableAddressTypes()}
        showPhoneNumber={true}
        title="Add Address"
        apiEndpoint={buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.addresses(merchant.id))}
        apiService={apiService}
      />

      {/* Edit Address Modal */}
      <AddressFormModal
        isOpen={showEditAddressModal}
        toggle={toggleEditAddressModal}
        onSave={(addressData) => {
          // Update local state and close modal
          setAddresses(prev => prev.map(addr => 
            addr.id === editingAddress.id ? { ...addressData, id: addr.id } : addr
          ));
          setShowEditAddressModal(false);
          setEditingAddress(null);
          showNotification('success', 'Address updated successfully!');
        }}
        addressData={editAddressData}
        availableAddressTypes={['PHYSICAL', 'MAILING', 'BILLING']}
        showPhoneNumber={true}
        title="Edit Address"
        apiEndpoint={editingAddress ? buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.addresses(merchant.id) + `/${editingAddress.id}`) : null}
        apiService={apiService}
      />

      {/* Delete Address Confirmation Modal */}
      <Modal isOpen={showDeleteAddressModal} toggle={toggleDeleteAddressModal}>
        <ModalHeader toggle={toggleDeleteAddressModal}>
          <h5 className="mb-0">
            <i className="ni ni-fat-remove mr-2 text-danger" />
            Confirm Deletion
          </h5>
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            Are you sure you want to delete the <strong>{deletingAddress?.addressType}</strong> address?
            <br />
            <small className="text-muted">
              {deletingAddress && getAddressDisplay(deletingAddress)}
            </small>
            <br />
            <strong>This action cannot be undone.</strong>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteAddressModal} disabled={deletingAddressLoading}>
            <i className="ni ni-fat-remove mr-2" />
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmDeleteAddress} disabled={deletingAddressLoading}>
            {deletingAddressLoading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              <>
                <i className="ni ni-fat-remove mr-2" />
                Delete Address
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default MerchantAddressesTab;
